/**
 * Radio Player for Quran Radio
 * Handles: HLS.js streaming, volume control, mute functionality
 */

import Hls from 'hls.js'
import type { PlayCallback, PauseCallback, LoadingCallback, LoadedCallback, PlayerErrorCallback } from './types'

export class RadioPlayer {
  private audio: HTMLAudioElement
  private hls: Hls | null = null
  private _currentChannelId: number | string | null = null
  private _currentStreamUrl: string | null = null
  private _isMuted = false
  private _previousVolume = 0.5

  // Event callbacks
  onPlay: PlayCallback | null = null
  onPause: PauseCallback | null = null
  onError: PlayerErrorCallback | null = null
  onLoading: LoadingCallback | null = null
  onLoaded: LoadedCallback | null = null

  constructor(audioElementId: string) {
    const audio = document.getElementById(audioElementId) as HTMLAudioElement | null
    if (!audio) {
      throw new Error(`Audio element with id "${audioElementId}" not found`)
    }
    this.audio = audio

    // Initialize audio settings
    this.audio.volume = this._previousVolume
    this.setupAudioEvents()
  }

  get currentChannelId(): number | string | null {
    return this._currentChannelId
  }

  get currentStreamUrl(): string | null {
    return this._currentStreamUrl
  }

  get isMuted(): boolean {
    return this._isMuted
  }

  private setupAudioEvents(): void {
    this.audio.addEventListener('play', () => {
      console.log('[RadioPlayer] Playing')
      if (this.onPlay) this.onPlay()
    })

    this.audio.addEventListener('pause', () => {
      console.log('[RadioPlayer] Paused')
      if (this.onPause) this.onPause()
    })

    this.audio.addEventListener('waiting', () => {
      console.log('[RadioPlayer] Loading/Buffering...')
      if (this.onLoading) this.onLoading()
    })

    this.audio.addEventListener('canplay', () => {
      console.log('[RadioPlayer] Can play')
      if (this.onLoaded) this.onLoaded()
    })

    this.audio.addEventListener('error', (e) => {
      console.error('[RadioPlayer] Audio error:', e)
      if (this.onError) {
        this.onError({
          type: 'audio_error',
          message: 'Failed to play audio stream',
          originalEvent: e,
        })
      }
    })
  }

  /**
   * Load and play HLS stream
   */
  playStream(url: string, channelId: number | string): void {
    console.log('[RadioPlayer] Loading stream:', url, 'Channel:', channelId)

    // Stop current playback
    this.stop()

    this._currentChannelId = channelId
    this._currentStreamUrl = url

    if (this.onLoading) this.onLoading()

    // Check if HLS is supported
    if (Hls.isSupported()) {
      this.playWithHls(url)
    } else if (this.audio.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      this.playNativeHls(url)
    } else {
      console.error('[RadioPlayer] HLS not supported')
      if (this.onError) {
        this.onError({
          type: 'hls_not_supported',
          message: 'HLS streaming is not supported in this browser',
        })
      }
    }
  }

  private playWithHls(url: string): void {
    // Destroy existing HLS instance
    if (this.hls) {
      this.hls.destroy()
      this.hls = null
    }

    // Create new HLS instance
    this.hls = new Hls({
      maxBufferLength: 30,
      maxMaxBufferLength: 60,
      startLevel: -1, // Auto quality
      capLevelToPlayerSize: true,
    })

    this.hls.loadSource(url)
    this.hls.attachMedia(this.audio)

    // HLS events
    this.hls.on(Hls.Events.MANIFEST_PARSED, (_event, _data) => {
      console.log('[RadioPlayer] Manifest parsed')
      this.audio.play().catch((err) => {
        console.warn('[RadioPlayer] Autoplay prevented:', err)
      })
    })

    this.hls.on(Hls.Events.ERROR, (_event, data) => {
      console.error('[RadioPlayer] HLS error:', data)

      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            console.log('[RadioPlayer] Network error, trying to recover...')
            this.hls?.startLoad()
            break
          case Hls.ErrorTypes.MEDIA_ERROR:
            console.log('[RadioPlayer] Media error, trying to recover...')
            this.hls?.recoverMediaError()
            break
          default:
            console.error('[RadioPlayer] Fatal error, cannot recover')
            if (this.onError) {
              this.onError({
                type: 'hls_fatal_error',
                message: 'Stream playback failed',
                details: data,
              })
            }
            break
        }
      }
    })

    this.hls.on(Hls.Events.FRAG_LOADED, () => {
      if (this.onLoaded) this.onLoaded()
    })
  }

  private playNativeHls(url: string): void {
    console.log('[RadioPlayer] Using native HLS support')
    this.audio.src = url
    this.audio.play().catch((err) => {
      console.warn('[RadioPlayer] Autoplay prevented:', err)
    })
  }

  /**
   * Stop playback
   */
  stop(): void {
    if (this.hls) {
      this.hls.destroy()
      this.hls = null
    }

    this.audio.pause()
    this.audio.src = ''
    this._currentChannelId = null
    this._currentStreamUrl = null
  }

  /**
   * Pause playback
   */
  pause(): void {
    if (this.audio) {
      this.audio.pause()
    }
  }

  /**
   * Resume playback
   */
  resume(): void {
    if (this.audio && this.audio.src) {
      this.audio.play().catch((err) => {
        console.warn('[RadioPlayer] Resume failed:', err)
      })
    }
  }

  /**
   * Set volume
   * @param volume - Volume level (0-100)
   */
  setVolume(volume: number): void {
    const normalizedVolume = Math.max(0, Math.min(100, volume)) / 100
    this.audio.volume = normalizedVolume

    // If volume is set while muted, unmute
    if (this._isMuted && volume > 0) {
      this._isMuted = false
    }

    console.log('[RadioPlayer] Volume set to:', normalizedVolume)
  }

  /**
   * Get current volume (0-100)
   */
  getVolume(): number {
    return Math.round(this.audio.volume * 100)
  }

  /**
   * Toggle mute
   */
  toggleMute(): boolean {
    if (this._isMuted) {
      this.audio.volume = this._previousVolume
      this._isMuted = false
      console.log('[RadioPlayer] Unmuted, volume:', this._previousVolume)
    } else {
      this._previousVolume = this.audio.volume
      this.audio.volume = 0
      this._isMuted = true
      console.log('[RadioPlayer] Muted')
    }
    return this._isMuted
  }

  /**
   * Set mute state
   */
  setMute(muted: boolean): void {
    if (muted && !this._isMuted) {
      this._previousVolume = this.audio.volume
      this.audio.volume = 0
      this._isMuted = true
    } else if (!muted && this._isMuted) {
      this.audio.volume = this._previousVolume
      this._isMuted = false
    }
  }

  /**
   * Check if currently playing
   */
  isPlaying(): boolean {
    return !this.audio.paused && this.audio.currentTime > 0
  }

  /**
   * Get audio element for visualizer
   */
  getAudioElement(): HTMLAudioElement {
    return this.audio
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stop()
  }
}